import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { requete } from '../../api/client';
import { messageErreur } from '../../api/erreurs';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { EnTetePage } from '../../commun/EnTetePage';
interface Plan {
  id: string;
  titre: string;
  operations: string;
  frequence_jours: number;
  prochaine_echeance: string;
  derniere_realisation: string | null;
  actif: boolean;
  version: number;
  equipement_nom: string;
  equipement_code: string;
  en_retard: boolean;
  bientot: boolean;
}
function Historique({ id }: { id: string }) {
  const q = useQuery({
    queryKey: ['maintenance', 'historique', id],
    queryFn: () =>
      requete<{ elements: { id: string; realise_le: string; notes: string }[] }>(
        `/patrimoine/maintenance/${id}/historique`,
      ),
  });
  return (
    <>
      {q.error && <p role="alert">{messageErreur(q.error)}</p>}
      <ol>
        {q.data?.elements.map((r) => (
          <li key={r.id}>
            {r.realise_le} — {r.notes}
          </li>
        ))}
      </ol>
    </>
  );
}
export function PageMaintenance() {
  const { contexte } = useSession();
  const client = useQueryClient();
  const [creation, setCreation] = useState(false);
  const [historique, setHistorique] = useState<string | null>(null);
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);
  const liste = useQuery({
    queryKey: ['maintenance', 'plans'],
    queryFn: () => requete<{ elements: Plan[] }>('/patrimoine/maintenance'),
    enabled: peut(contexte, 'maintenance', 'voir'),
  });
  const equipements = useQuery({
    queryKey: ['patrimoine', 'equipements'],
    queryFn: () =>
      requete<{ elements: { id: string; nom: string; code: string; statut: string }[] }>(
        '/patrimoine/equipements',
      ),
    enabled: creation,
  });
  if (!peut(contexte, 'maintenance', 'voir'))
    return <p role="alert">Accès à la maintenance non autorisé.</p>;
  return (
    <section>
      <EnTetePage titre="Maintenance préventive" />
      {peut(contexte, 'maintenance', 'creer') && (
        <button onClick={() => setCreation(!creation)}>
          {creation ? 'Fermer le formulaire' : 'Créer un programme'}
        </button>
      )}
      {creation && (
        <form
          style={{ display: 'grid', gap: 12, maxWidth: 700, marginBlock: 20 }}
          onSubmit={async (e) => {
            e.preventDefault();
            const d = new FormData(e.currentTarget);
            setErreur('');
            setEnCours(true);
            try {
              await requete('/patrimoine/maintenance', {
                methode: 'POST',
                corps: {
                  equipement_id: d.get('equipement_id'),
                  titre: d.get('titre'),
                  operations: d.get('operations'),
                  frequence_jours: Number(d.get('frequence_jours')),
                  prochaine_echeance: d.get('prochaine_echeance'),
                },
              });
              setCreation(false);
              await client.invalidateQueries({ queryKey: ['maintenance'] });
            } catch (err) {
              setErreur(messageErreur(err));
            } finally {
              setEnCours(false);
            }
          }}
        >
          <label>
            Équipement{' '}
            <select name="equipement_id" required>
              <option value="">Choisir</option>
              {equipements.data?.elements
                .filter((e) => e.statut !== 'DECOMMISSIONED')
                .map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.code} — {e.nom}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Titre <input name="titre" required minLength={3} maxLength={150} />
          </label>
          <label>
            Opérations prévues{' '}
            <textarea name="operations" required minLength={3} maxLength={4000} />
          </label>
          <label>
            Fréquence en jours{' '}
            <input name="frequence_jours" type="number" required min={1} max={3650} step={1} />
          </label>
          <label>
            Prochaine échéance <input name="prochaine_echeance" type="date" required />
          </label>
          <button disabled={enCours}>Enregistrer le programme</button>
          {equipements.error && <p role="alert">{messageErreur(equipements.error)}</p>}
        </form>
      )}
      {erreur && <p role="alert">{erreur}</p>}
      {liste.error && <p role="alert">{messageErreur(liste.error)}</p>}
      {liste.isPending && <p role="status">Chargement…</p>}
      {liste.data && <p>{liste.data.elements.length} programme(s), maximum 500.</p>}
      {liste.data?.elements.map((p) => (
        <article key={p.id} style={{ paddingBlock: 20, borderBottom: '1px solid currentColor' }}>
          <h2>{p.titre}</h2>
          <p>
            {p.equipement_code} — {p.equipement_nom}
          </p>
          <p>{p.operations}</p>
          <p>
            Échéance : {p.prochaine_echeance} · tous les {p.frequence_jours} jours ·{' '}
            {!p.actif
              ? 'Suspendu'
              : p.en_retard
                ? 'En retard'
                : p.bientot
                  ? 'À réaliser prochainement'
                  : 'Planifié'}
          </p>
          {peut(contexte, 'maintenance', 'modifier') && (
            <form
              key={p.version}
              style={{ display: 'grid', gap: 12, maxWidth: 700 }}
              onSubmit={async (e) => {
                e.preventDefault();
                const d = new FormData(e.currentTarget);
                const action = d.get('action');
                setErreur('');
                setEnCours(true);
                try {
                  await requete(
                    `/patrimoine/maintenance/${p.id}/${action === 'realisation' ? 'realisation' : 'activation'}`,
                    {
                      methode: 'POST',
                      corps:
                        action === 'realisation'
                          ? {
                              version: p.version,
                              realise_le: d.get('realise_le'),
                              notes: d.get('notes'),
                            }
                          : { version: p.version, actif: !p.actif, motif: d.get('notes') },
                    },
                  );
                  await client.invalidateQueries({ queryKey: ['maintenance'] });
                } catch (err) {
                  setErreur(messageErreur(err));
                } finally {
                  setEnCours(false);
                }
              }}
            >
              <label>
                Action{' '}
                <select name="action">
                  {p.actif && <option value="realisation">Enregistrer une réalisation</option>}
                  <option value="activation">
                    {p.actif ? 'Suspendre le programme' : 'Réactiver le programme'}
                  </option>
                </select>
              </label>
              {p.actif && (
                <label>
                  Date de réalisation <input name="realise_le" type="date" />
                </label>
              )}
              <label>
                Compte rendu ou motif{' '}
                <textarea name="notes" required minLength={3} maxLength={4000} />
              </label>
              <button disabled={enCours}>Enregistrer l’action</button>
            </form>
          )}
          <button
            onClick={() => setHistorique(historique === p.id ? null : p.id)}
            aria-expanded={historique === p.id}
          >
            Historique des réalisations
          </button>
          {historique === p.id && <Historique id={p.id} />}
        </article>
      ))}
    </section>
  );
}
